import clsx from 'clsx';
import { DateTime } from 'luxon';
import { reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { formatDurationRussian } from '@/utils/formatDurationRussian';

import { DragSelectionOverlay } from '../DragSelectionOverlay/DragSelectionOverlay';
import { DragSelectionPopover } from '../DragSelectionPopover/DragSelectionPopover';
import { EventBlock } from '../EventBlock';
import { useDragSelection } from '../../hooks/useDragSelection';
import { useCalendarViewStore } from '../../store/CalendarViewContext';
import type { CalendarEventWithColor, DayColumn, TimeGridProps } from '../../types';
import { CALENDAR_VIEW_CONSTANTS } from '../../types';
import { getDayStatusIntervals } from '../../utils/dayStatusIntervals';

import s from './TimeGrid.module.scss';

const { HOUR_HEIGHT, HOURS_IN_DAY, DEFAULT_SCROLL_HOUR } = CALENDAR_VIEW_CONSTANTS;

const HOURS = Array.from({ length: HOURS_IN_DAY }, (_, i) => i);

/** Interval for updating the current time indicator (ms) */
const CURRENT_TIME_UPDATE_INTERVAL = 30_000;

function formatTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

const noop = () => {};

const TimeGrid: React.FC<TimeGridProps> = observer(
  ({
    days,
    onEventClick,
    initialScrollHour = DEFAULT_SCROLL_HOUR,
    showCreateDraftOption = true,
    readOnly = false,
  }) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const store = useCalendarViewStore();
    const { dragState, handleMouseDown, selection, popoverAnchor, dismissPopover, clearSelection } =
      useDragSelection(scrollContainerRef);

    // Scroll to initial hour on mount only
    useEffect(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top: initialScrollHour * HOUR_HEIGHT,
          behavior: 'instant',
        });
      }
    }, []);

    // Clear selection overlay when drag-log modal closes
    useEffect(() => {
      return reaction(
        () => store.dragLogData,
        (data) => {
          if (!data) clearSelection();
        },
      );
    }, [store, clearSelection]);

    const handleLogTime = useCallback(() => {
      if (!selection) return;

      const { date, startMinutes, durationMinutes } = selection;

      store.openDragLog(date, formatTime(startMinutes), formatDurationRussian(durationMinutes));

      dismissPopover();
    }, [selection, dismissPopover, store]);

    const handleCreateDraft = useCallback(() => {
      if (!selection) return;

      const { date, startMinutes, durationMinutes } = selection;

      store.openDragCreateDraft(
        date,
        formatTime(startMinutes),
        formatDurationRussian(durationMinutes),
      );

      dismissPopover();
    }, [selection, dismissPopover, store]);

    return (
      <div
        className={clsx(s.time_grid, { [s.time_grid_dragging]: dragState.isDragging })}
        ref={scrollContainerRef}
      >
        <div className={s.time_grid__container}>
          {/* Time labels column */}
          <div className={s.time_grid__time_column}>
            {HOURS.map((hour) => (
              <div key={hour} className={s.time_grid__time_label} style={{ height: HOUR_HEIGHT }}>
                {formatHour(hour)}
              </div>
            ))}
          </div>

          {/* Day columns */}
          <div className={s.time_grid__days}>
            {days.map((day, dayIndex) => (
              <DayColumnComponent
                key={dayIndex}
                day={day}
                onEventClick={onEventClick}
                onMouseDown={readOnly ? noop : handleMouseDown}
                dragState={dragState}
                hasSelection={selection !== null}
              />
            ))}
          </div>
        </div>

        <DragSelectionPopover
          open={popoverAnchor !== null}
          anchorPosition={popoverAnchor}
          onLogTime={handleLogTime}
          onCreateDraft={handleCreateDraft}
          onClose={clearSelection}
          showCreateDraftOption={showCreateDraftOption}
        />
      </div>
    );
  },
);

interface DayColumnComponentProps {
  day: DayColumn;
  onEventClick: (event: CalendarEventWithColor) => void;
  onMouseDown: (e: React.MouseEvent, dayDate: DateTime) => void;
  dragState: { isDragging: boolean; dayDate: DateTime | null; startY: number; currentY: number };
  hasSelection: boolean;
}

const DayColumnComponent: React.FC<DayColumnComponentProps> = observer(
  ({ day, onEventClick, onMouseDown, dragState, hasSelection }) => {
    const isToday = day.date.hasSame(DateTime.now(), 'day');
    const isWeekend = day.date.weekday >= 6;
    const isActiveDragColumn =
      dragState.dayDate !== null && dragState.dayDate.hasSame(day.date, 'day');
    const showOverlay = isActiveDragColumn && (dragState.isDragging || hasSelection);
    const statusSegments = getDayStatusIntervals(day.date, day.timedEvents);

    const handleColumnMouseDown = (e: React.MouseEvent) => {
      onMouseDown(e, day.date);
    };

    return (
      <div
        className={clsx(s.time_grid__day_column, {
          [s.time_grid__day_column_today]: isToday,
          [s.time_grid__day_column_weekend]: isWeekend && !isToday,
        })}
        onMouseDown={handleColumnMouseDown}
      >
        {/* Hour grid lines */}
        {HOURS.map((hour) => (
          <div key={hour} className={s.time_grid__hour_cell} style={{ height: HOUR_HEIGHT }} />
        ))}

        {/* Полоса статуса дня: залогировано (зелёный), встреча без лога (красный), свободно (серый) */}
        <div className={s.time_grid__status_bar} aria-hidden>
          {statusSegments.map((seg, idx) => {
            const topPx = (seg.startMinutes / 60) * HOUR_HEIGHT;
            const heightPx = ((seg.endMinutes - seg.startMinutes) / 60) * HOUR_HEIGHT;

            return (
              <div
                key={idx}
                className={clsx(s.time_grid__status_bar_segment, {
                  [s.time_grid__status_bar_segment_free]: seg.status === 'free',
                  [s.time_grid__status_bar_segment_logged]: seg.status === 'logged',
                  [s.time_grid__status_bar_segment_unlogged]: seg.status === 'unlogged_meeting',
                })}
                style={{ top: topPx, height: heightPx }}
              />
            );
          })}
        </div>

        {/* Events (CalDav + other logs, all positioned by the same layout pipeline) */}
        <div className={s.time_grid__events}>
          {day.timedEvents.map((positionedEvent) => (
            <EventBlock
              key={positionedEvent.event.id}
              event={positionedEvent}
              onClick={onEventClick}
            />
          ))}
        </div>

        {/* Drag selection overlay */}
        {showOverlay && (
          <DragSelectionOverlay
            top={dragState.startY}
            height={dragState.currentY - dragState.startY}
            visible
          />
        )}

        {/* Current time indicator */}
        {isToday && <CurrentTimeIndicator />}
      </div>
    );
  },
);

const CurrentTimeIndicator: React.FC = () => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), CURRENT_TIME_UPDATE_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  const top = ((now.getHours() * 60 + now.getMinutes()) / 60) * HOUR_HEIGHT;

  return (
    <div className={s.time_grid__current_time} style={{ top }}>
      <div className={s.time_grid__current_time_dot} />
      <div className={s.time_grid__current_time_line} />
    </div>
  );
};

function formatHour(hour: number): string {
  return `${hour.toString().padStart(2, '0')}:00`;
}

export { TimeGrid };
